import axios, { type AxiosInstance } from 'axios';
import config from '@/config';
import { createErrorFromResponse } from '@/lib/errors';
import type {
  Session,
  Event,
  AgentRunRequest,
  AgentRunResponse,
  HealthStatus,
  Artifact,
  FeedbackStatus,
  CorpusResponse,
  Action,
  CreateActionRequest,
  UpdateActionRequest,
  ActionCorpusFile,
  ActionRun,
  ActionRunListResponse,
  ActionRunStatistics,
  TestActionResponse,
} from '@/types';

// Global variable to store the token getter function
let getClerkToken: (() => Promise<string | null>) | null = null;

export function setClerkTokenGetter(tokenGetter: () => Promise<string | null>) {
  getClerkToken = tokenGetter;
}

export class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: config.API_BASE_URL,
      timeout: 60000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor for authentication with Clerk JWT token
    this.client.interceptors.request.use(
      async (config) => {
        if (getClerkToken) {
          try {
            const token = await getClerkToken();
            if (token) {
              config.headers.Authorization = `Bearer ${token}`;
            }
          } catch (error) {
            console.error('Error getting Clerk token:', error);
          }
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        const appError = createErrorFromResponse(error, 'API Request');
        console.error('API Error:', appError);

        if (appError.statusCode === 401) {
          // TODO: Redirect to login or refresh token when Clerk is implemented
          console.error('Authentication error - redirect to login');
        }

        return Promise.reject(appError);
      }
    );
  }

  // Health Check
  async healthCheck(): Promise<HealthStatus> {
    const response = await this.client.get<HealthStatus>('/health');
    return response.data;
  }

  // Corpus Management
  async getCorpus(): Promise<CorpusResponse> {
    try {
      const response = await this.client.get<CorpusResponse>('/corpus');
      return response.data;
    } catch (error: any) {
      // If 404, return empty corpus instead of throwing error
      if (error?.statusCode === 404 || error?.response?.status === 404) {
        return {
          company: null,
          shared: null,
          resolvedAt: new Date().toISOString()
        };
      }
      throw error;
    }
  }

  // Get available corpora, models, and tools for agent configuration
  async getAvailableOptions(): Promise<{
    corpora: Array<{
      corpus_id: string;
      name: string;
      type: 'company' | 'shared';
      file_count?: number;
      description: string;
    }>;
    models: Array<{
      id: string;
      name: string;
      description: string;
      model_id: string;
    }>;
    tools: Array<{
      id: string;
      name: string;
      description: string;
    }>;
  }> {
    const response = await this.client.get('/api/available-corpora');
    return response.data;
  }

  // Session Management
  async getSessions(): Promise<Session[]> {
    const response = await this.client.get<Session[]>('/sessions');
    return response.data;
  }

  async getSession(sessionId: string): Promise<Session> {
    const response = await this.client.get<Session>(
      `/sessions/${sessionId}`
    );
    return response.data;
  }

  async createSession(state?: Record<string, any>): Promise<Session> {
    const response = await this.client.post<Session>(
      '/sessions',
      state ? { state } : {}
    );
    return response.data;
  }

  async createSessionWithId(
    sessionId: string,
    state?: Record<string, any>
  ): Promise<Session> {
    const response = await this.client.post<Session>(
      `/sessions/${sessionId}`,
      state ? { state } : {}
    );
    return response.data;
  }

  async deleteSession(sessionId: string): Promise<void> {
    await this.client.delete(`/sessions/${sessionId}`);
  }

  // Agent Communication
  async sendMessage(request: AgentRunRequest): Promise<AgentRunResponse> {
    // Convert camelCase to snake_case for backend
    const backendRequest = {
      session_id: request.sessionId,
      new_message: request.newMessage,
      streaming: request.streaming || false,
      tool_config: request.toolConfig, // Add tool configuration
    };
    const response = await this.client.post<{ sessionId: string; events: Event[] }>('/run', backendRequest);
    return response.data;
  }

  // Server-Sent Events for streaming
  async *streamMessage(request: AgentRunRequest): AsyncGenerator<Event> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Add authorization header if Clerk token is available
    if (getClerkToken) {
      try {
        const token = await getClerkToken();
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }
      } catch (error) {
        console.error('Error getting Clerk token for streaming:', error);
      }
    }

    // Convert camelCase to snake_case for backend
    const backendRequest = {
      session_id: request.sessionId,
      new_message: request.newMessage,
      streaming: request.streaming || false,
    };

    const response = await fetch(`${config.API_BASE_URL}/run_sse`, {
      method: 'POST',
      headers,
      body: JSON.stringify(backendRequest),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('No response body reader available');
    }

    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data.trim()) {
              try {
                const event = JSON.parse(data) as Event;
                yield event;
              } catch (error) {
                console.error('Error parsing SSE data:', error, data);
              }
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  }

  // Artifact Management
  async getArtifact(
    sessionId: string,
    artifactName: string,
    version?: number
  ): Promise<Artifact> {
    const url = version
      ? `/sessions/${sessionId}/artifacts/${artifactName}/versions/${version}`
      : `/sessions/${sessionId}/artifacts/${artifactName}`;

    const response = await this.client.get(url);
    return response.data;
  }

  async listArtifacts(sessionId: string): Promise<string[]> {
    const response = await this.client.get<string[]>(
      `/sessions/${sessionId}/artifacts`
    );
    return response.data;
  }

  async listArtifactVersions(
    sessionId: string,
    artifactName: string
  ): Promise<number[]> {
    const response = await this.client.get<number[]>(
      `/sessions/${sessionId}/artifacts/${artifactName}/versions`
    );
    return response.data;
  }

  async deleteArtifact(
    sessionId: string,
    artifactName: string
  ): Promise<void> {
    await this.client.delete(
      `/sessions/${sessionId}/artifacts/${artifactName}`
    );
  }

  // Feedback Management
  async submitFeedback(
    sessionId: string,
    eventId: string,
    feedbackType: 'like' | 'dislike'
  ): Promise<void> {
    try {
      // Submit feedback to backend API
      const response = await this.client.post(
        `/sessions/${sessionId}/events/${eventId}/feedback`,
        { 
          feedback_type: feedbackType,
          timestamp: new Date().toISOString()
        }
      );

      console.log('Feedback submitted:', { sessionId, eventId, feedbackType });
      return response.data;
    } catch (error) {
      console.error('Error submitting feedback:', error);
      throw createErrorFromResponse(error, 'Submit Feedback');
    }
  }

  async clearFeedback(
    sessionId: string,
    eventId: string
  ): Promise<void> {
    try {
      // Clear feedback from backend API
      await this.client.delete(
        `/sessions/${sessionId}/events/${eventId}/feedback`
      );

      console.log('Feedback cleared:', { sessionId, eventId });
    } catch (error) {
      console.error('Error clearing feedback:', error);
      throw createErrorFromResponse(error, 'Clear Feedback');
    }
  }

  async getFeedback(_sessionId: string, eventId: string): Promise<FeedbackStatus> {
    try {
      // Note: Backend doesn't have a GET endpoint for individual feedback
      // Feedback is stored at the event level and loaded with the session
      // This method is kept for compatibility but returns null
      // Feedback should be loaded from session events
      return {
        eventId,
        type: null,
      };
    } catch (error) {
      console.error('Error getting feedback:', error);
      return {
        eventId,
        type: null,
      };
    }
  }

  // Clear all feedback for a session (utility method)
  async clearSessionFeedback(sessionId: string): Promise<void> {
    try {
      // Note: Backend doesn't have a bulk clear endpoint
      // Individual feedback must be cleared per event
      console.log(`Session feedback clearing not implemented for session ${sessionId}`);
    } catch (error) {
      console.error('Error clearing session feedback:', error);
    }
  }

  // Action Management
  async getActions(): Promise<Action[]> {
    try {
      // Backend now returns camelCase via Pydantic serialization aliases
      const response = await this.client.get<Action[]>('/api/actions');
      return response.data;
    } catch (error) {
      console.error('Error fetching actions:', error);
      throw createErrorFromResponse(error, 'Get Actions');
    }
  }

  async getAction(actionId: string): Promise<Action> {
    try {
      // Backend now returns camelCase via Pydantic serialization aliases
      const response = await this.client.get<Action>(`/api/actions/${actionId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching action ${actionId}:`, error);
      throw createErrorFromResponse(error, 'Get Action');
    }
  }

  async createAction(request: CreateActionRequest): Promise<Action> {
    try {
      // Validate required fields
      if (!request.actionName?.trim()) {
        throw new Error('Action name is required');
      }
      
      if (!request.prompt?.trim()) {
        throw new Error('Prompt is required');
      }
      
      // Validate fileName (can be string or array)
      if (Array.isArray(request.fileName)) {
        if (request.fileName.length === 0) {
          throw new Error('At least one file must be selected');
        }
      } else if (!request.fileName?.trim()) {
        throw new Error('File name is required');
      }
      
      if (!request.frequency) {
        throw new Error('Frequency is required');
      }

      // Backend now accepts camelCase via Pydantic aliases
      const response = await this.client.post<Action>('/api/actions', request);

      return response.data;
    } catch (error) {
      console.error('Error creating action:', error);
      throw createErrorFromResponse(error, 'Create Action');
    }
  }

  async updateAction(actionId: string, request: UpdateActionRequest): Promise<Action> {
    try {
      // Validate actionId
      if (!actionId?.trim()) {
        throw new Error('Action ID is required');
      }

      // Validate fields if provided
      if (request.actionName !== undefined && !request.actionName.trim()) {
        throw new Error('Action name cannot be empty');
      }
      if (request.prompt !== undefined && !request.prompt.trim()) {
        throw new Error('Prompt cannot be empty');
      }
      if (request.fileName !== undefined) {
        if (Array.isArray(request.fileName)) {
          if (request.fileName.length === 0) {
            throw new Error('At least one file must be selected');
          }
        } else if (!request.fileName.trim()) {
          throw new Error('File name cannot be empty');
        }
      }

      // Ensure at least one field is being updated
      if (Object.keys(request).length === 0) {
        throw new Error('At least one field must be provided for update');
      }

      // Backend now accepts camelCase via Pydantic aliases
      const response = await this.client.put<Action>(`/api/actions/${actionId}`, request);

      return response.data;
    } catch (error) {
      console.error(`Error updating action ${actionId}:`, error);
      throw createErrorFromResponse(error, 'Update Action');
    }
  }

  async deleteAction(actionId: string): Promise<void> {
    try {
      // Validate actionId
      if (!actionId?.trim()) {
        throw new Error('Action ID is required');
      }

      await this.client.delete(`/api/actions/${actionId}`);
    } catch (error) {
      console.error(`Error deleting action ${actionId}:`, error);
      throw createErrorFromResponse(error, 'Delete Action');
    }
  }

  async triggerAction(actionId: string): Promise<{ status: string; actionId: string; message: string; timestamp: string }> {
    try {
      // Validate actionId
      if (!actionId?.trim()) {
        throw new Error('Action ID is required');
      }

      const response = await this.client.post<any>(`/api/actions/${actionId}/trigger`);
      return response.data;
    } catch (error) {
      console.error(`Error triggering action ${actionId}:`, error);
      throw createErrorFromResponse(error, 'Trigger Action');
    }
  }

  // File listing for action dropdown population
  async getCorpusFiles(): Promise<ActionCorpusFile[]> {
    try {
      const corpusResponse = await this.getCorpus();

      // Use company corpus files
      if (!corpusResponse?.company?.files) {
        console.warn('No files found in company corpus');
        return [];
      }

      return corpusResponse.company.files.map((file, index) => ({
        id: `file_${index}`,
        name: file.displayName,
        displayName: file.displayName.replace(/\.[^/.]+$/, '') // Remove extension for display
      }));
    } catch (error) {
      console.error('Error getting corpus files for actions:', error);
      throw createErrorFromResponse(error, 'Get Corpus Files');
    }
  }

  // Document Upload
  async uploadDocument(file: File): Promise<any> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await this.client.post('/api/upload/pdf', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data;
    } catch (error) {
      console.error('Error uploading document:', error);
      throw createErrorFromResponse(error, 'Upload Document');
    }
  }

  // Action Run History
  async getActionRuns(
    actionId: string,
    params?: {
      limit?: number;
      offset?: number;
      status?: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
    }
  ): Promise<ActionRunListResponse> {
    try {
      const response = await this.client.get<ActionRunListResponse>(
        `/api/actions/${actionId}/runs`,
        { params }
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching runs for action ${actionId}:`, error);
      throw createErrorFromResponse(error, 'Get Action Runs');
    }
  }

  async getAllActionRuns(params?: {
    limit?: number;
    offset?: number;
    status?: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  }): Promise<ActionRunListResponse> {
    try {
      const response = await this.client.get<ActionRunListResponse>(
        '/api/action-runs',
        { params }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching all action runs:', error);
      throw createErrorFromResponse(error, 'Get All Action Runs');
    }
  }

  async getActionRun(runId: string): Promise<ActionRun> {
    try {
      const response = await this.client.get<ActionRun>(
        `/api/action-runs/${runId}`
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching action run ${runId}:`, error);
      throw createErrorFromResponse(error, 'Get Action Run');
    }
  }

  async getActionStatistics(actionId: string): Promise<ActionRunStatistics> {
    try {
      const response = await this.client.get<ActionRunStatistics>(
        `/api/actions/${actionId}/statistics`
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching statistics for action ${actionId}:`, error);
      throw createErrorFromResponse(error, 'Get Action Statistics');
    }
  }

  async testAction(request: CreateActionRequest): Promise<TestActionResponse> {
    try {
      // Validate required fields (same as createAction)
      if (!request.actionName?.trim()) {
        throw new Error('Action name is required');
      }
      
      if (!request.prompt?.trim()) {
        throw new Error('Prompt is required');
      }
      
      // Validate fileName
      if (Array.isArray(request.fileName)) {
        if (request.fileName.length === 0) {
          throw new Error('At least one file must be selected');
        }
      } else if (!request.fileName?.trim()) {
        throw new Error('File name is required');
      }

      if (!request.frequency) {
        throw new Error('Frequency is required');
      }

      // Test run always sends email (async like trigger)
      const response = await this.client.post<TestActionResponse>(
        '/api/actions/test-run',
        request
      );
      return response.data;
    } catch (error) {
      console.error('Error triggering test action:', error);
      throw createErrorFromResponse(error, 'Test Action');
    }
  }

}

// Create a singleton instance
export const apiClient = new ApiClient();